import { useState } from 'react';
import type { FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { clsx } from 'clsx';
import { AuthShell } from '../components/AuthShell.tsx';
import { ErrorNotice, Field, Spinner } from '../components/ui.tsx';
import { ApiRequestError, api } from '../lib/api.ts';
import { useAuth } from '../lib/auth.tsx';

export function RegisterPage(): React.JSX.Element {
  const { refresh } = useAuth();
  const navigate = useNavigate();

  const [form, setForm] = useState({ fullname: '', email: '', password: '' });
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);

  const update = (key: keyof typeof form) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setForm((previous) => ({ ...previous, [key]: event.target.value }));
  };

  const handleSubmit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    setError(null);
    setFieldErrors({});
    setSubmitting(true);

    try {
      await api.post('/auth/register', form);
      // Registration signs you in, so pick up the new session before routing.
      await refresh();
      navigate('/chats', { replace: true });
    } catch (caught) {
      if (caught instanceof ApiRequestError) {
        setError(caught.message);
        setFieldErrors(caught.fieldErrors);
      } else {
        setError('Something went wrong');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthShell
      title="Create your workspace"
      subtitle="One place for every conversation your customers start."
      footer={
        <>
          Already have an account?{' '}
          <Link to="/login" className="font-medium text-brand-600 hover:underline dark:text-brand-400">
            Sign in
          </Link>
        </>
      }
    >
      <form className="space-y-4" onSubmit={(event) => void handleSubmit(event)} noValidate>
        {error ? <ErrorNotice message={error} /> : null}

        <Field label="Full name" htmlFor="fullname" error={fieldErrors.fullname}>
          <input
            id="fullname"
            className={clsx('lg-input', fieldErrors.fullname && 'lg-input-error')}
            autoComplete="name"
            autoFocus
            required
            value={form.fullname}
            onChange={update('fullname')}
          />
        </Field>

        <Field label="Work email" htmlFor="email" error={fieldErrors.email}>
          <input
            id="email"
            type="email"
            className={clsx('lg-input', fieldErrors.email && 'lg-input-error')}
            autoComplete="username"
            required
            value={form.email}
            onChange={update('email')}
          />
        </Field>

        <Field
          label="Password"
          htmlFor="password"
          error={fieldErrors.password}
          hint="At least 8 characters."
        >
          <input
            id="password"
            type="password"
            className={clsx('lg-input', fieldErrors.password && 'lg-input-error')}
            autoComplete="new-password"
            required
            minLength={8}
            value={form.password}
            onChange={update('password')}
          />
        </Field>

        <button type="submit" className="lg-btn-primary w-full" disabled={submitting}>
          {submitting ? <Spinner /> : null}
          Create workspace
        </button>
      </form>
    </AuthShell>
  );
}
